import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { useHorizontalDragScroll } from '@crossnokaye/ux-common';

interface UseTableDragScrollOptions {
  enabled: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

interface UseTableDragScrollReturn {
  isDragging: boolean;
  handlers: Record<string, (e: React.MouseEvent) => void>;
  handleClickCapture: (e: React.MouseEvent) => void;
}

const CLICK_PREVENT_DRAG_THRESHOLD_PX = 10;

/**
 * Hook for drag-to-scroll functionality in DataTable.
 *
 * Wraps ux-common's useHorizontalDragScroll with additional click prevention
 * to ensure row clicks don't fire during/after drag scrolling.
 *
 * @param options.enabled - Whether drag scrolling is enabled
 * @returns Container ref, drag state, wrapped handlers, and click capture handler
 *
 * @example
 * ```tsx
 * const { containerRef, isDragging, handlers, handleClickCapture } = useTableDragScroll({
 *   enabled: dragScroll,
 * });
 *
 * return (
 *   <div
 *     ref={containerRef}
 *     {...handlers}
 *     onClickCapture={handleClickCapture}
 *   >
 *     {children}
 *   </div>
 * );
 * ```
 */
export const useTableDragScroll = ({
  enabled,
  containerRef,
}: UseTableDragScrollOptions): UseTableDragScrollReturn => {
  // Use ux-common's horizontal drag scroll hook
  const {
    containerRef: dragRef,
    isDragging,
    handlers,
  } = useHorizontalDragScroll({
    enabled,
    scrollSpeed: 1,
    dragThreshold: 10,
    hideScrollbars: false,
    elasticity: true,
    defaultCursor: 'grab',
    momentumDecay: 1,
  });

  // Sync the external containerRef into ux-common's internal dragRef so its
  // scroll calculations reference the actual mounted DOM element.
  // Intentionally runs every render — containerRef.current may change after mount.
  useEffect(() => {
    if (containerRef.current) {
      (dragRef as MutableRefObject<HTMLDivElement | null>).current = containerRef.current;
    }
  });

  // Track if an actual drag occurred (mouse moved while down)
  // We need our own tracking because React state updates are async
  const dragTrackingRef = useRef({
    isMouseDown: false,
    hasMoved: false,
    shouldPreventClick: false,
    startX: 0,
    startY: 0,
  });

  // Wrap the handlers to track drag state ourselves
  const wrappedHandlers = useMemo(() => {
    if (!enabled) return {};

    return {
      ...handlers,
      onMouseDown: (e: React.MouseEvent) => {
        dragTrackingRef.current = {
          isMouseDown: true,
          hasMoved: false,
          shouldPreventClick: false,
          startX: e.clientX,
          startY: e.clientY,
        };
        handlers.onMouseDown(e);
      },
      onMouseMove: (e: React.MouseEvent) => {
        if (dragTrackingRef.current.isMouseDown) {
          const deltaX = Math.abs(e.clientX - dragTrackingRef.current.startX);
          const deltaY = Math.abs(e.clientY - dragTrackingRef.current.startY);
          const movedPastThreshold = Math.max(deltaX, deltaY) >= CLICK_PREVENT_DRAG_THRESHOLD_PX;

          if (movedPastThreshold) {
            dragTrackingRef.current.hasMoved = true;
            dragTrackingRef.current.shouldPreventClick = true;
          }
        }
        handlers.onMouseMove(e);
      },
      onMouseUp: () => {
        dragTrackingRef.current.isMouseDown = false;
        // Keep shouldPreventClick true - it will be reset after click is handled
        handlers.onMouseUp();
      },
      onMouseLeave: () => {
        dragTrackingRef.current.isMouseDown = false;
        handlers.onMouseLeave();
      },
    };
  }, [enabled, handlers]);

  // Capture clicks and prevent if we just dragged
  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (dragTrackingRef.current.shouldPreventClick) {
      e.stopPropagation();
      e.preventDefault();
      dragTrackingRef.current.shouldPreventClick = false;
    }
  }, []);

  return {
    isDragging,
    handlers: wrappedHandlers,
    handleClickCapture: enabled ? handleClickCapture : () => {},
  };
};
